import { useTranslation } from "react-i18next";
import { Hero } from "@ucl/dse/hero";
import { Text } from "@ucl/dse/text";
import { LinkButton } from "src/components/link-button/link-button";
import { LinkType } from "src/types/general";
import promoImg from "../../../assets/ford-puma-promo-card.jpeg";
import { MedalliaSurvey } from "src/components/medallia-survey/medallia-survey";
import styles from "./success-card.module.scss";
import { useNumberFlag } from "src/consts/flagWrapper";
import { Flags } from "src/consts/flags";

interface SuccessCardProps {
    customerName: string;
    dealerConfig: LinkType;
    promotionsConfig: LinkType;
}

export const SuccessCard = ({ customerName, dealerConfig, promotionsConfig }: SuccessCardProps) => {
    const { t } = useTranslation();
    const apr: number = useNumberFlag(Flags.APR_REPRESENTATIVE);

    return (
        <>
            <div data-testid="resultsSuccessCard" className={styles.successCard}>
                <div className={styles.successCardWrapper}>
                    <h1>
                        {customerName}
                        {t("creditDecision.success.resultsHeader")}
                    </h1>
                    <svg viewBox="0 0 200 20" fill="none" aria-hidden="true" focusable="false">
                        <path d="M0 15 C40 5, 160 5, 200 15" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    <p className={styles.successCardBody}>{t("creditDecision.success.resultsBody")}</p>
                    {!!apr && (
                        <p data-testid="apr-representative" className={styles.aprValue}>
                            {apr + t("creditDecision.success.aprRepresentative")}
                        </p>
                    )}
                    <p className={styles.aprDisclaimer}>{t("creditDecision.success.aprDisclaimer")}</p>
                </div>
            </div>

            <div className={styles.dealerInfoCard}>
                <div className={styles.dealerCardWrapper}>
                    <Text.H2 fontWeight="semi-bold" data-testid="dealer-subheading">
                        {t("creditDecision.success.dealerCardHeading")}
                    </Text.H2>
                    <Text.P>{t("creditDecision.success.dealerCardBody")}</Text.P>
                    <Text.P size="small">{t("creditDecision.success.dealerCardBody1")}</Text.P>
                </div>

                <div className={styles.dealerCardButtonContainer}>
                    <LinkButton
                        config={dealerConfig}
                        ariaLabel={
                            t("creditDecision.success.speakToDealerButton") + " " + t("general.openNewTabAriaLabel")
                        }
                    >
                        {t("creditDecision.success.speakToDealerButton")}
                    </LinkButton>
                </div>
            </div>

            <MedalliaSurvey />

            <div className={styles.promotionsSection}>
                <Text.H2 fontWeight="semi-bold" data-testid="promotions-subheading">
                    {t("creditDecision.success.promotionsHeading")}
                </Text.H2>
                <p className={styles.promotionsDescription}>{t("creditDecision.success.promotionsDescription")}</p>

                <Hero surface="dark" contentAlignment="bottom-left" className={styles.promotionsHero}>
                    <Hero.Image src={promoImg} alt="Ford Puma SUV" />
                    <Hero.Contents className={styles.heroContents}>
                        <p className={styles.heroTitle}>{t("creditDecision.success.promotionsHeroTitle")}</p>
                        <LinkButton
                            config={promotionsConfig}
                            variant="secondary"
                            style={{ color: "white", borderColor: "white", fontSize: "medium" }}
                        >
                            {t("creditDecision.success.explorePromotionsButton")}
                        </LinkButton>
                    </Hero.Contents>
                </Hero>
            </div>
        </>
    );
};
